import { Droplets, Filter, Layers } from 'lucide-react';
import EquipmentAdminValidation from '@/components/EquipmentAdminValidation';

export default function AdminMultimediaValidation() {
  return (
    <EquipmentAdminValidation
      title="Multimedia Filter Validation — Admin Audit"
      description="Ergun head loss, filtration efficiency, and backwash physics. Every equation, every value, every check."
      buildSections={(p) => {
        const flowRate = Number(p.flowRate ?? 500);
        const influentTSS = Number(p.tss ?? 80);
        const influentCOD = Number(p.cod ?? 300);
        const temperature = Number(p.temp ?? 20);
        const og = Number(p.og ?? 120);
        const fmt = (v: any, d = 2) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

        // Default geometry for validation
        const vesselDiameter = 2.5;
        const anthraciteDepth = 0.5, sandDepth = 0.25, garnetDepth = 0.15, freeboard = 0.5;
        const bedDepth = anthraciteDepth + sandDepth + garnetDepth;
        const area = Math.PI * Math.pow(vesselDiameter / 2, 2);
        const mediaVolume = area * bedDepth;
        const totalVolume = area * (bedDepth + freeboard);
        const q = flowRate / 24;
        const approachVelocity = q / area;
        const tempK = temperature + 273.15;
        const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
        const rhoWater = 998 - 0.2 * (temperature - 20);

        const voidFractions = [0.48, 0.40, 0.36];
        const particleSizes = [1.4, 0.55, 0.3];
        const layerDepths = [anthraciteDepth, sandDepth, garnetDepth];
        const bulkDensities = [1100, 2650, 4100];
        const layerNames = ['Anthracite', 'Sand', 'Garnet'];
        const layerResults = [];
        let totalHeadLoss = 0;
        for (let i = 0; i < 3; i++) {
          const d = particleSizes[i] / 1000;
          const eps = voidFractions[i];
          const L = layerDepths[i];
          const u = approachVelocity / 3600;
          const term1 = (150 * Math.pow(1 - eps, 2) * mu * u) / (Math.pow(eps, 3) * d * d);
          const term2 = (1.75 * (1 - eps) * rhoWater * u * u) / (Math.pow(eps, 3) * d);
          const hl = ((term1 + term2) / 9810) * L;
          totalHeadLoss += hl;
          const vTerminal = Math.sqrt(4 * 9.81 * d * (bulkDensities[i] - rhoWater) / (3 * 0.44 * rhoWater));
          const backwashVel = 25 / 3600;
          const expansionPct = backwashVel > 0 ? ((backwashVel / vTerminal) * 100) : 0;
          layerResults.push({
            name: layerNames[i],
            es: particleSizes[i],
            porosity: voidFractions[i],
            depth: L,
            headLoss: hl,
            expansionPct: Math.min(50, expansionPct),
          });
        }

        const mediaEffFactor = Math.min(1.0, (bedDepth / 0.6) * 0.8);
        const tssRemoval = Math.min(0.95, mediaEffFactor * (0.50 + (influentTSS > 200 ? 0.15 : 0)));
        const codRemoval = Math.min(0.40, mediaEffFactor * 0.25);
        const solidsCaptured = (flowRate * influentTSS * tssRemoval) / 1000;
        const cloggingRate = solidsCaptured / mediaVolume;
        const backwashRate = 25;
        const backwashFlow = area * backwashRate;
        const backwashVolume = totalVolume * 3;
        const backwashDuration = (backwashVolume / backwashFlow) * 60;
        const power = (backwashFlow * backwashRate * 9.81) / (3600 * 1000 * 0.7);
        const gValue = Math.sqrt(power * 1000 / (mu * totalVolume));

        return [
          {
            title: 'Ergun Head Loss — Layer by Layer',
            icon: Layers,
            equations: [
              { label: 'Flow (hourly)', formula: 'Q = flowRate / 24', substitution: `Q = ${fmt(flowRate, 0)} / 24 = ${fmt(q, 1)} m³/h`, result: fmt(q, 1), status: 'pass', unit: 'm³/h' },
              { label: 'Vessel area', formula: 'A = π × (D/2)²', substitution: `A = π × (${vesselDiameter}/2)² = ${fmt(area, 2)} m²`, result: fmt(area, 2), status: 'pass', unit: 'm²' },
              { label: 'Approach velocity', formula: 'v = Q / A', substitution: `v = ${fmt(q, 1)} / ${fmt(area, 2)} = ${fmt(approachVelocity, 1)} m/h`, result: fmt(approachVelocity, 1), status: approachVelocity <= 12 ? 'pass' : approachVelocity <= 20 ? 'warn' : 'fail', unit: 'm/h' },
              { label: 'Bed depth', formula: 'L = anthracite + sand + garnet', substitution: `L = ${anthraciteDepth} + ${sandDepth} + ${garnetDepth} = ${fmt(bedDepth, 2)} m`, result: fmt(bedDepth, 2), status: 'pass', unit: 'm' },
              ...layerResults.map((l: any) => ({
                label: `${l.name} head loss`,
                formula: 'Δh = [(150(1-ε)²μv)/(ε³d²) + 1.75(1-ε)ρv²/(ε³d)] × L / 9810',
                substitution: `Δh = ${fmt(l.headLoss, 4)} m`,
                result: fmt(l.headLoss, 4),
                status: l.headLoss < 1.5 ? 'pass' : l.headLoss < 3 ? 'warn' : 'fail',
                unit: 'm',
              })),
              { label: 'Total head loss', formula: 'ΣΔh', substitution: `ΣΔh = ${fmt(totalHeadLoss, 3)} m`, result: fmt(totalHeadLoss, 3), status: totalHeadLoss <= 2 ? 'pass' : totalHeadLoss <= 4 ? 'warn' : 'fail', unit: 'm' },
            ],
          },
          {
            title: 'Filtration Efficiency',
            icon: Filter,
            equations: [
              { label: 'Media efficiency factor', formula: 'f = min(1.0, (L/0.6) × 0.8)', substitution: `f = min(1.0, (${fmt(bedDepth, 2)}/0.6) × 0.8) = ${fmt(mediaEffFactor, 2)}`, result: fmt(mediaEffFactor, 2), status: 'pass', unit: '' },
              { label: 'TSS removal', formula: 'η = min(0.95, f × (0.50 + (TSS>200 ? 0.15 : 0)))', substitution: `η = min(0.95, ${fmt(mediaEffFactor, 2)} × ${fmt(0.50 + (influentTSS > 200 ? 0.15 : 0), 2)}) = ${fmt(tssRemoval * 100, 0)}%`, result: fmt(tssRemoval * 100, 0), status: tssRemoval >= 0.50 ? 'pass' : 'warn', unit: '%' },
              { label: 'COD removal', formula: 'η_COD = min(0.40, f × 0.25)', substitution: `η_COD = min(0.40, ${fmt(mediaEffFactor, 2)} × 0.25) = ${fmt(codRemoval * 100, 0)}%`, result: fmt(codRemoval * 100, 0), status: 'pass', unit: '%' },
              { label: 'Solids captured', formula: 'm = flowRate × TSS × η / 1000', substitution: `m = ${fmt(flowRate, 0)} × ${influentTSS} × ${fmt(tssRemoval, 2)} / 1000 = ${fmt(solidsCaptured, 1)} kg/d`, result: fmt(solidsCaptured, 1), status: 'pass', unit: 'kg/d' },
              { label: 'Clogging rate', formula: 'C = m / mediaVolume', substitution: `C = ${fmt(solidsCaptured, 1)} / ${fmt(mediaVolume, 1)} = ${fmt(cloggingRate, 2)} kg/m³·d`, result: fmt(cloggingRate, 2), status: 'pass', unit: 'kg/m³·d' },
            ],
          },
          {
            title: 'Backwash Physics',
            icon: Droplets,
            equations: [
              { label: 'Backwash rate', formula: 'v_bw = 25 m³/m²·h', substitution: `v_bw = 25 m³/m²·h`, result: '25', status: 'pass', unit: 'm³/m²·h' },
              { label: 'Backwash flow', formula: 'Q_bw = A × v_bw', substitution: `Q_bw = ${fmt(area, 2)} × 25 = ${fmt(backwashFlow, 1)} m³/h`, result: fmt(backwashFlow, 1), status: 'pass', unit: 'm³/h' },
              { label: 'Backwash volume', formula: 'V_bw = totalVolume × 3', substitution: `V_bw = ${fmt(totalVolume, 1)} × 3 = ${fmt(backwashVolume, 1)} m³`, result: fmt(backwashVolume, 1), status: 'pass', unit: 'm³' },
              { label: 'Backwash duration', formula: 't = (V_bw / Q_bw) × 60', substitution: `t = (${fmt(backwashVolume, 1)} / ${fmt(backwashFlow, 1)}) × 60 = ${fmt(backwashDuration, 0)} min`, result: fmt(backwashDuration, 0), status: 'pass', unit: 'min' },
              { label: 'Pump power', formula: 'P = (Q_bw × v_bw × 9.81) / (3600 × 1000 × 0.7)', substitution: `P = (${fmt(backwashFlow, 1)} × 25 × 9.81) / (3600 × 1000 × 0.7) = ${fmt(power, 3)} kW`, result: fmt(power, 3), status: 'pass', unit: 'kW' },
              { label: 'Shear rate (G)', formula: 'G = √(P × 1000 / (μ × V))', substitution: `G = √(${fmt(power, 3)} × 1000 / (${fmt(mu, 5)} × ${fmt(totalVolume, 1)})) = ${fmt(gValue, 0)} s⁻¹`, result: fmt(gValue, 0), status: 'pass', unit: 's⁻¹' },
            ],
            warnings: [
              ...(og > 500 ? ['⚠️ High O&G (>500 mg/L) — may foul multimedia filter media. Consider pre-DAF.'] : []),
            ],
          },
        ];
      }}
    />
  );
}
